import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { effectiveAvatarUrl } from '@/lib/avatar';
import { circleShortName } from '@/lib/format';
import { sendConnectionRequest } from '@/lib/connections';
import { findContactsOnHeirapsia, type MatchedContact } from '@/lib/contactsMatch';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';

export default function ContactsMatchesScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [contacts, setContacts] = useState<MatchedContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await findContactsOnHeirapsia();
      setContacts(data);
    } catch {
      setError(t('contactsMatch.loadError'));
    } finally {
      setLoading(false);
    }
  }

  async function onConnect(userId: string) {
    setBusyId(userId);
    try {
      await sendConnectionRequest(userId);
      setContacts((prev) => prev.map((c) => (c.id === userId ? { ...c, connection_status: 'pending_sent' } : c)));
    } catch {
      // no-op
    } finally {
      setBusyId(null);
    }
  }

  return (
    <View style={[styles.root, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View style={styles.headerTitle}>
          <Ionicons name="people-outline" size={20} color={colors.text} />
          <Text style={styles.headerText}>{t('contactsMatch.title')}</Text>
        </View>
        <View style={{ width: 24 }} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.primary} size="large" />
        </View>
      ) : error ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>{error}</Text>
        </View>
      ) : contacts.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="people-outline" size={40} color={colors.textMuted} />
          <Text style={styles.emptyText}>{t('contactsMatch.empty')}</Text>
        </View>
      ) : (
        <FlatList
          data={contacts}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          renderItem={({ item }) => {
            const displayName = item.full_name?.trim() ? circleShortName(item.full_name, item.nickname) : t('common.user');
            return (
              <Pressable
                style={styles.row}
                onPress={() => router.push({ pathname: '/(app)/users/[id]', params: { id: item.id } })}
                accessibilityRole="button"
              >
                {item.avatar_url ? (
                  <Image source={{ uri: effectiveAvatarUrl(item) ?? item.avatar_url }} style={styles.avatar} />
                ) : (
                  <View style={styles.avatarFallback}>
                    <Text style={styles.avatarInitial}>{displayName.charAt(0).toUpperCase()}</Text>
                  </View>
                )}
                <View style={styles.rowBody}>
                  <View style={styles.nameRow}>
                    <Text style={styles.name} numberOfLines={1}>{displayName}</Text>
                    {item.verified ? (
                      <VerifiedBadge compact />
                    ) : item.phone_verified ? (
                      <VerifiedBadge compact kind="phone" />
                    ) : null}
                  </View>
                  {item.user_number != null && (
                    <Text style={styles.userNumber}>HEI-{String(item.user_number).padStart(5, '0')}</Text>
                  )}
                </View>
                {item.connection_status === 'accepted' ? (
                  <View style={styles.statusPill}>
                    <Ionicons name="checkmark-circle" size={14} color={colors.success} />
                    <Text style={styles.statusPillText}>{t('contactsMatch.connected')}</Text>
                  </View>
                ) : item.connection_status === 'pending_sent' ? (
                  <View style={styles.statusPill}>
                    <Text style={styles.statusPillText}>{t('contactsMatch.pending')}</Text>
                  </View>
                ) : item.connection_status === 'pending_received' ? (
                  <View style={styles.statusPill}>
                    <Text style={styles.statusPillText}>{t('contactsMatch.pendingReceived')}</Text>
                  </View>
                ) : (
                  <Pressable
                    style={styles.connectBtn}
                    onPress={() => onConnect(item.id)}
                    disabled={busyId === item.id}
                    accessibilityRole="button"
                  >
                    {busyId === item.id ? (
                      <ActivityIndicator size="small" color={colors.background} />
                    ) : (
                      <Text style={styles.connectBtnText}>{t('contactsMatch.connect')}</Text>
                    )}
                  </Pressable>
                )}
              </Pressable>
            );
          }}
        />
      )}
    </View>
  );
}

const makeStyles = (colors: AppColors) => StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.lg, paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border,
  },
  headerTitle: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  headerText: { fontSize: fontSize.lg, fontWeight: '700', color: colors.text },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingHorizontal: spacing.xl },
  emptyText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center' },
  listContent: { padding: spacing.lg, gap: spacing.sm },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: colors.surface, borderRadius: radius.lg, padding: spacing.sm,
    borderWidth: 1, borderColor: colors.border,
  },
  avatar: { width: 44, height: 44, borderRadius: 22 },
  avatarFallback: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
  },
  avatarInitial: { color: colors.primary, fontSize: fontSize.md, fontWeight: '700' },
  rowBody: { flex: 1, gap: 2 },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  name: { fontSize: fontSize.sm, fontWeight: '600', color: colors.text, flexShrink: 1 },
  userNumber: { fontSize: fontSize.xs, color: colors.textMuted },
  connectBtn: {
    backgroundColor: colors.primary, borderRadius: radius.md,
    paddingHorizontal: spacing.md, paddingVertical: spacing.xs,
  },
  connectBtnText: { color: colors.background, fontSize: fontSize.xs, fontWeight: '700' },
  statusPill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
  },
  statusPillText: { fontSize: fontSize.xs, color: colors.textMuted, fontWeight: '600' },
});
